import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { Loader } from '../../Common';
import { useGetUserListQuery } from '../../../api/userApi.js';
import { isAdminSelector } from '../../../selectors/authSelectors.js';
import { Button, Stack, Typography } from '@mui/material';
import BlueDotIcon from '../../../assets/img/blue-dot.svg?react';
import baseApi, { TAGS } from '../../../api/baseApi.js';
import { useAddBlueDotsMutation, useGetNumBlueDotsQuery } from '../../../api/bookkeeperAssignmentsApi.js';

const BlueDotBtn = () => {
  const isAdmin = useSelector(isAdminSelector);

  const dispatch = useDispatch();

  const { data: userList = [], isLoading: isUserListLoading } = useGetUserListQuery();
  const { data: numBlueDots, isLoading: isNumBlueDotsLoading } = useGetNumBlueDotsQuery();

  const [addBlueDots, { isLoading: isAddBlueDotsLoading }] = useAddBlueDotsMutation();

  if (isUserListLoading || isNumBlueDotsLoading) {
    return null;
  }

  const handleRefresh = async () => {
    dispatch(
      baseApi.util.invalidateTags([
        TAGS.EXPENSES,
        TAGS.FILES,
        TAGS.NUM_BLUE_DOTS,
        TAGS.TAX_PRO_CONSULTATION_MEETING_INFO,
        TAGS.UNNOTICED_USERS,
        TAGS.USER_ACCOUNTS,
        TAGS.USER_BOOKKEEPER_ASSIGNMENTS,
        TAGS.USER_INFO,
        TAGS.USER_LIST,
        TAGS.USER_MESSAGES,
        TAGS.USER_REMINDERS
      ])
    );
  };

  const handleAddBlueDots = async () => {
    try {
      const { data } = await addBlueDots();
      const { maxReached, outstandingBlue, noMoreBlueDots } = data;

      if (outstandingBlue) {
        Swal.fire({
          icon: 'warning',
          title: 'Blue dots left',
          text: `Please answer all blue dots before requesting more 🙏`
        });
      } else if (noMoreBlueDots && !outstandingBlue) {
        Swal.fire('Woohoo!', 'No more blue dots!', 'success');
      } else if (maxReached) {
        Swal.fire({
          icon: 'warning',
          title: 'Max blue dots reached',
          text: 'Take a break!'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Problem adding blue dots',
        text: 'Please try again later. If the problem persists, please make a ticket with the engineering team.'
      });
    }
  };

  const userText = userList.length === 1 ? 'user' : 'users';

  return (
    <>
      {isAddBlueDotsLoading ? (
        <div className='userListLoader'>
          <Loader />
        </div>
      ) : (
        <>
          <Stack
            direction='row'
            sx={{
              alignItems: 'center',
              gap: 1,
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              mx: 1
            }}>
            <Stack
              direction='row'
              sx={{
                alignItems: 'center',
                gap: 1
              }}>
              <Typography variant='body2'>
                {userList.length} {!isAdmin ? `/ ${numBlueDots}` : userText}
              </Typography>
              <Button
                variant='outlined'
                color='primary'
                onClick={handleRefresh}
                sx={{
                  borderRadius: '30px',
                  ml: 1
                }}>
                <Stack
                  direction='row'
                  sx={{
                    alignItems: 'center',
                    gap: 1
                  }}>
                  <Typography variant='body2' color='primary'>
                    Refresh
                  </Typography>
                </Stack>
              </Button>
              {isAdmin ? (
                <Typography
                  variant='body2'
                  color='secondary'
                  sx={{
                    cursor: 'default',
                    padding: '5px 10px'
                  }}>
                  ADMIN
                </Typography>
              ) : (
                <Button
                  variant='outlined'
                  color='primary'
                  onClick={handleAddBlueDots}
                  sx={{
                    borderRadius: '30px'
                  }}>
                  <Stack
                    direction='row'
                    sx={{
                      alignItems: 'center',
                      gap: 1
                    }}>
                    <Typography variant='body1' color='primary'>
                      Add
                    </Typography>
                    <BlueDotIcon width={12} height={12} />
                  </Stack>
                </Button>
              )}
            </Stack>
          </Stack>
        </>
      )}
    </>
  );
};

export default BlueDotBtn;
